import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Card, CardSection } from '@/lib/v2/components';
import { Table } from '@/lib/v3/components';
import { TRANSLATIONS_TABLE } from '@/lib/v3/components/Table/Table';

import { ActionBar } from '@/src/modules/AutomationModuleNew/components/ActionBarButtons';
import DeleteRulesModal from '@/src/modules/AutomationModuleNew/components/ActionBarRules/DeleteRulesModal';
import DuplicateRulesModal from '@/src/modules/AutomationModuleNew/components/ActionBarRules/DuplicateRulesModal';
import RenameRulesModal from '@/src/modules/AutomationModuleNew/components/ActionBarRules/RenameRulesModal';
import { EmptyState } from '@/src/modules/AutomationModuleNew/components/EmptyState';
import { FiltersTable } from '@/src/modules/AutomationModuleNew/components/FiltersTable';
import { HeaderTable } from '@/src/modules/AutomationModuleNew/components/HeaderTable';
import { AUTOMATION_PATHS } from '@/src/modules/AutomationModuleNew/constants';
import { useCreateRule, useRulesFilters } from '@/src/modules/AutomationModuleNew/hooks/Rules';
import { useActionBar } from '@/src/modules/AutomationModuleNew/hooks/useActionBar';
import { useRulesTableColumns } from '@/src/modules/AutomationModuleNew/hooks/useRulesTableColumns';
import { RulesTable as RulesTableType } from '@/src/modules/AutomationModuleNew/interfaces/automation';
import { SelectRuleTypeModal } from '@/src/modules/AutomationModuleNew/screens/RulesHome/components';

const RulesTable = () => {
  const { t } = useTranslation();
  const { columns, actionRowMenuData } = useRulesTableColumns();
  const navigate = useNavigate();
  const { selectRuleTypeModalIsOpen, handleToggleSelectRuleTypeModal } = useCreateRule();

  const {
    data,
    dropDownItems,
    getInitialData,
    hasFiltersApplied,
    isLoading,
    orderColumn,
    valuePagination,
    rowCount,
    handlePaginationChange,
    handleOrderColumnChange,
    rowSelectionChange,
    countDataList,
    totalRowSelected,
    filters,
    handleStatusFilterChange,
    handleDateChange,
    handleSearchChange,
    resetFilters,
    handleStatusFilterApply,
    handleSearchApply,
    rowSelection,
    dataSelected,
  } = useRulesFilters();

  const {
    deleteModalIsOpen,
    toggleDeleteModal,
    editModalIsOpen,
    toggleEditModal,
    duplicateModalIsOpen,
    toggleDuplicateModal,
  } = useActionBar({
    totalRowSelected,
  });

  const handleGoDetail = useCallback(
    (rowData: RulesTableType) => {
      const { campaignId, id } = rowData;
      navigate(
        `/v2/${AUTOMATION_PATHS.MODULE_PATH}/${AUTOMATION_PATHS.RULES_PATH}/${campaignId}/action/${id}/email-trigger`
      );
    },
    [navigate]
  );

  useEffect(() => {
    void getInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const rowDataSelected = actionRowMenuData ?? dataSelected;

  const rulesIDs = rowDataSelected.map((msg: RulesTableType) => msg.id);
  const ruleName = rowDataSelected.length > 0 ? rowDataSelected[0].name : '';
  const ruleId = rowDataSelected.length > 0 ? rowDataSelected[0].id : 0;

  return (
    <Card className="mt-6">
      <CardSection noPadding>
        <div className="flex flex-col py-4">
          <HeaderTable
            buttonText={t('RulesTable.header.buttonText')}
            hasFiltersApplied={hasFiltersApplied}
            isOpenModal={false}
            partial={countDataList.partial}
            title={t('RulesTable.header.title')}
            total={countDataList.total}
            onClickButton={handleToggleSelectRuleTypeModal}
            onCloseModal={() => {}}
          />
          <FiltersTable
            actionBarComponent={<ActionBar totalRowSelected={totalRowSelected} />}
            dropdownItemsFilter={dropDownItems}
            enableActionBar={totalRowSelected > 0}
            endDate={filters.creationDateBefore}
            isClearButtonDisabled={!hasFiltersApplied}
            placeholderDate={t('RECURRENT_MESSAGE.FILTERS.date')}
            placeholderInput={t('RulesTable.filters.rules.placeholderInput')}
            startDate={filters.creationDateAfter}
            totalRowSelected={totalRowSelected}
            valueInputSearch={filters.search}
            onChangeCheckbox={handleStatusFilterChange}
            onChangeDate={handleDateChange}
            onChangeInputSearch={handleSearchChange}
            onClearFilters={resetFilters}
            onClickApplyFilter={handleStatusFilterApply}
            onClickApplySearch={handleSearchApply}
          />
          <Table
            enableStickyHeader
            columns={columns}
            data={data}
            emptyState={{
              hasFiltersApplied,
              noDataResultComponent: (
                <EmptyState text={t('MessagesContent.noDataResultComponent')} />
              ),
              noFilterResultComponent: (
                <EmptyState text={t('MessagesContent.noFilterResultComponent')} />
              ),
            }}
            isLoading={isLoading}
            valueOrderColumn={orderColumn}
            valueRowsSelection={rowSelection}
            withPagination={{
              valuePagination,
              rowCount,
              onPaginationChange: handlePaginationChange,
              label: t(TRANSLATIONS_TABLE.showRows),
            }}
            onOrderColumnChange={handleOrderColumnChange}
            onRowClick={handleGoDetail}
            onRowsSelectionChange={rowSelectionChange}
          />
        </div>
        {deleteModalIsOpen && (
          <DeleteRulesModal
            ids={rulesIDs}
            isOpen={deleteModalIsOpen}
            refetchData={getInitialData}
            totalRowSelected={totalRowSelected}
            onClose={toggleDeleteModal}
          />
        )}
        {editModalIsOpen && (
          <RenameRulesModal
            id={ruleId}
            isOpen={editModalIsOpen}
            name={ruleName}
            refetchData={getInitialData}
            onClose={toggleEditModal}
          />
        )}
        {duplicateModalIsOpen && (
          <DuplicateRulesModal
            id={ruleId}
            isOpen={duplicateModalIsOpen}
            name={ruleName}
            refetchData={getInitialData}
            onClose={toggleDuplicateModal}
          />
        )}
        {selectRuleTypeModalIsOpen && (
          <SelectRuleTypeModal
            isOpen={selectRuleTypeModalIsOpen}
            onClose={handleToggleSelectRuleTypeModal}
          />
        )}
      </CardSection>
    </Card>
  );
};

export default RulesTable;
